import React, { useMemo, useRef, useState, useEffect } from 'react';
import { format } from 'date-fns';
import { Check, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { StaffMember } from './types';

interface CalendarDayStatsProps {
    date: Date;
    staffMembers: StaffMember[];
    bookings: any[];
    busySlots: any[];
    shiftChanges: any[];
    organization: any;
    getPerformanceById: (id: string) => any;
    getBookingPrice: (booking: any) => number;
}

type PerStaff = Record<string, number>;

interface StatDef {
    key: string;
    label: string;
    perStaff: PerStaff;
    format: (v: number) => string;
    valueClass: (v: number) => string;
}

const WEEKDAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const toMin = (v: any): number | null => {
    if (!v) return null;
    const [h, m] = String(v).slice(0, 5).split(':').map(Number);
    return h * 60 + (m || 0);
};

/**
 * Barra riepilogo del giorno (stile Clip Workspace): Totale, Appuntamenti, No-show,
 * Occupazione, Sconti. Ogni voce apre un dettaglio per barbiere con selezione.
 */
const CalendarDayStats: React.FC<CalendarDayStatsProps> = ({
    date, staffMembers, bookings, busySlots, shiftChanges, organization, getPerformanceById, getBookingPrice,
}) => {
    const { t } = useTranslation();
    const dStr = format(date, 'yyyy-MM-dd');

    const stats = useMemo(() => {
        const totale: PerStaff = {};
        const appuntamenti: PerStaff = {};
        const noshow: PerStaff = {};
        const sconti: PerStaff = {};
        const bookedMin: PerStaff = {};
        const availMin: PerStaff = {};

        const dow = WEEKDAYS[date.getDay()];
        const shopH = organization?.time_ranges?.[dow];

        staffMembers.forEach((s) => {
            totale[s.id] = 0; appuntamenti[s.id] = 0; noshow[s.id] = 0; sconti[s.id] = 0; bookedMin[s.id] = 0; availMin[s.id] = 0;
        });

        for (const b of bookings) {
            if (b.date !== dStr || !(b.hairdresser_id in totale)) continue;
            const dur = getPerformanceById(b.performance_id)?.duration || 30;
            if (b.status === 'confirmed') {
                totale[b.hairdresser_id] += getBookingPrice(b);
                appuntamenti[b.hairdresser_id] += 1;
                bookedMin[b.hairdresser_id] += dur;
                const adj = Number(b.price_adjustment) || 0;
                if (adj < 0) sconti[b.hairdresser_id] += -adj;
            } else if (b.status === 'no_show') {
                noshow[b.hairdresser_id] += 1;
            }
        }

        // Minuti disponibili per barbiere: orario del giorno (ShiftChange > orari settimanali,
        // intersecati con il negozio) meno pausa, ferie giornaliere azzerano.
        staffMembers.forEach((s: any) => {
            const holiday = (busySlots || []).some((bs: any) =>
                bs.type === 'holiday' && (bs.hairdresser_id === s.id || bs.hairdresser_id === null) &&
                bs.start_date <= dStr && (bs.end_date || bs.start_date) >= dStr);
            if (holiday) return;

            // Finestre di disponibilità extra: minuti in più anche fuori orario / a salone chiuso
            let extraMin = 0;
            for (const p of (busySlots || [])) {
                if (p.type !== 'extra' || p.hairdresser_id !== s.id) continue;
                if ((p.start_date || '').slice(0, 10) !== dStr) continue;
                const ps = toMin(p.start_time), pe = toMin(p.end_time);
                if (ps !== null && pe !== null && pe > ps) extraMin += pe - ps;
            }

            const sc = (shiftChanges || []).find((x: any) => x.hairdresser_id === s.id && x.shift_date === dStr);
            let open: number | null, close: number | null, bs: number | null, be: number | null;
            if (sc) {
                open = toMin(sc.start_time); close = toMin(sc.end_time);
                bs = toMin(sc.start_break); be = toMin(sc.end_break);
            } else {
                const eh = s.time_ranges?.[dow];
                if (!eh?.open || !shopH?.open) { availMin[s.id] = extraMin; return; }
                open = Math.max(toMin(eh.open)!, toMin(shopH.open)!);
                close = Math.min(toMin(eh.close)!, toMin(shopH.close)!);
                bs = toMin(eh.start_break); be = toMin(eh.end_break);
            }
            if (open === null || close === null || open >= close) { availMin[s.id] = extraMin; return; }
            let m = close - open;
            if (bs !== null && be !== null && be > bs) m -= Math.min(be, close) - Math.max(bs, open);
            // Permessi e blocchi puntuali riducono la disponibilità
            for (const p of (busySlots || [])) {
                if (p.hairdresser_id !== s.id) continue;
                if (p.type !== 'permit' && p.type !== 'block') continue;
                if (p.start_date > dStr || (p.end_date || p.start_date) < dStr) continue;
                const ps = toMin(p.start_time), pe = toMin(p.end_time);
                if (ps === null || pe === null) continue;
                const st = Math.max(ps, open), en = Math.min(pe, close);
                if (en > st) m -= en - st;
            }
            availMin[s.id] = Math.max(0, m) + extraMin;
        });

        return { totale, appuntamenti, noshow, sconti, bookedMin, availMin };
    }, [bookings, busySlots, shiftChanges, staffMembers, organization, dStr, date, getPerformanceById, getBookingPrice]);

    const defs: StatDef[] = [
        {
            key: 'total',
            label: t('calendar.stats.total', 'Totale oggi'),
            perStaff: stats.totale,
            format: (v) => `€${Math.round(v)}`,
            valueClass: () => 'text-clip-600',
        },
        {
            key: 'count',
            label: t('calendar.stats.count', 'Appuntamenti'),
            perStaff: stats.appuntamenti,
            format: (v) => String(v),
            valueClass: () => 'text-gray-900',
        },
        {
            key: 'noshow',
            label: t('calendar.stats.noshow', 'No-show'),
            perStaff: stats.noshow,
            format: (v) => String(v),
            valueClass: (v) => (v > 0 ? 'text-red-500' : 'text-gray-900'),
        },
        {
            key: 'occupancy',
            label: t('calendar.stats.occupancy', 'Occupazione'),
            perStaff: stats.bookedMin,
            format: () => '', // calcolata a parte (rapporto, non somma)
            valueClass: () => 'text-gray-900',
        },
        {
            key: 'discounts',
            label: t('calendar.stats.discounts', 'Sconti'),
            perStaff: stats.sconti,
            format: (v) => (v > 0 ? `-€${Math.round(v)}` : '€0'),
            valueClass: (v) => (v > 0 ? 'text-emerald-600' : 'text-gray-900'),
        },
    ];

    return (
        <div className="flex items-center flex-wrap gap-x-3 gap-y-0.5 px-3 py-1 md:gap-x-4 md:px-4 md:py-1.5 bg-clip-50/40 border-b border-gray-100">
            {defs.map((def) => (
                <StatChip
                    key={def.key}
                    def={def}
                    staffMembers={staffMembers}
                    occupancy={def.key === 'occupancy' ? { booked: stats.bookedMin, avail: stats.availMin } : undefined}
                />
            ))}
        </div>
    );
};

const StatChip: React.FC<{
    def: StatDef;
    staffMembers: StaffMember[];
    occupancy?: { booked: PerStaff; avail: PerStaff };
}> = ({ def, staffMembers, occupancy }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [selected, setSelected] = useState<Record<string, boolean>>(() =>
        Object.fromEntries(staffMembers.map((s) => [s.id, true])));
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const close = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    // Nuovi dipendenti (o cambi lista): includili di default
    useEffect(() => {
        setSelected((prev) => {
            const next = { ...prev };
            staffMembers.forEach((s) => { if (!(s.id in next)) next[s.id] = true; });
            return next;
        });
    }, [staffMembers]);

    const ids = staffMembers.filter((s) => selected[s.id] !== false).map((s) => s.id);

    const valueFor = (idList: string[]): string => {
        if (occupancy) {
            const b = idList.reduce((sum, id) => sum + (occupancy.booked[id] || 0), 0);
            const a = idList.reduce((sum, id) => sum + (occupancy.avail[id] || 0), 0);
            return a > 0 ? `${Math.round((b / a) * 100)}%` : '—';
        }
        return def.format(idList.reduce((sum, id) => sum + (def.perStaff[id] || 0), 0));
    };

    const totalValue = valueFor(ids);
    const numericTotal = ids.reduce((sum, id) => sum + (def.perStaff[id] || 0), 0);

    return (
        <div className="relative flex-shrink-0" ref={ref}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 text-xs font-medium text-gray-500 hover:text-gray-800 transition-colors"
            >
                {def.label}: <span className={`font-bold ${def.valueClass(numericTotal)}`}>{totalValue}</span>
                <ChevronDown size={12} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                /* Su mobile il pannello è centrato sullo schermo (fixed), da md in su è ancorato al chip */
                <div className="fixed left-0 right-0 top-48 mx-auto w-64 md:absolute md:top-7 md:left-0 md:right-auto md:mx-0 md:w-56 z-50 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-in fade-in slide-in-from-top-2 duration-150">
                    <p className="px-4 pb-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        {t('calendar.stats.select_staff', 'Seleziona barbieri')}
                    </p>
                    {staffMembers.map((s) => {
                        const on = selected[s.id] !== false;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => setSelected((prev) => ({ ...prev, [s.id]: !on }))}
                                className="w-full flex items-center gap-2.5 px-4 py-2 text-sm hover:bg-gray-50 transition-colors"
                            >
                                <span className={`w-4 h-4 rounded flex items-center justify-center border flex-shrink-0 ${on ? 'bg-clip-500 border-clip-500 text-white' : 'border-gray-300 bg-white'}`}>
                                    {on && <Check size={11} />}
                                </span>
                                <span className="flex-1 text-left text-gray-700 truncate">{s.name}</span>
                                <span className="text-xs font-bold text-gray-500 flex-shrink-0">{valueFor([s.id])}</span>
                            </button>
                        );
                    })}
                    <div className="flex items-center justify-between px-4 pt-2 mt-1 border-t border-gray-50 text-xs">
                        <span className="text-gray-500 font-medium">{t('calendar.stats.selected_total', 'Totale selezionati')}</span>
                        <span className={`font-bold ${def.valueClass(numericTotal)}`}>{totalValue}</span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CalendarDayStats;
